import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Check, ArrowLeft, Gift, ShieldCheck, Truck, Sparkles, Minus, Plus } from "lucide-react";
import { api, money } from "../lib/api";
import { useCart } from "../context/CartContext";
import { ProductReviews } from "../components/site/ProductReviews";

export default function ProductDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [img, setImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [missing, setMissing] = useState(false);
  const { add, gift, setGift } = useCart();

  useEffect(() => {
    setImg(0);
    api.get(`/products/${id}`).then((r) => setP(r.data)).catch(() => setMissing(true));
  }, [id]);

  useEffect(() => {
    if (!p || window.location.hash) return;
    requestAnimationFrame(() => document.getElementById("product-title")?.scrollIntoView({ block: "start" }));
  }, [p]);

  if (missing) return <div className="max-w-3xl mx-auto px-6 py-24 text-center"><p>Product not found.</p><Link to="/shop" className="text-[#0D9488]">Back to shop</Link></div>;
  if (!p) return <div className="min-h-[50vh]" />;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12" data-testid="product-detail-page">
      <Link to="/shop" data-testid="product-back-link" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-[#0D9488]"><ArrowLeft className="w-4 h-4" /> All products</Link>
      <div className="mt-6 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="bg-white rounded-[24px] border border-[#E4DFD4] overflow-hidden aspect-[4/3]">
            <img src={p.images[img]} alt={p.name} className="w-full h-full object-contain" />
          </div>
          {p.images.length > 1 && (
            <div className="mt-3 flex gap-3">
              {p.images.map((src, i) => (
                <button key={src} data-testid={`product-thumb-${i}`} onClick={() => setImg(i)} className={`w-20 h-20 rounded-xl overflow-hidden border-2 ${i === img ? "border-[#0D9488]" : "border-transparent"}`}>
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#0D9488]">{p.badge}</span>
          <h1 id="product-title" className="scroll-mt-36 font-display text-3xl sm:text-4xl font-semibold mt-3 leading-tight" data-testid="product-name">{p.name}</h1>
          <div className="font-mono text-3xl font-bold mt-5" data-testid="product-price">{money(p.price)}</div>
          <p className="mt-6 text-slate-700 leading-relaxed">{p.description}</p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <div className="flex items-center border border-[#E4DFD4] rounded-full bg-white">
              <button data-testid="product-qty-dec" onClick={() => setQty(Math.max(1, qty - 1))} className="p-3"><Minus className="w-4 h-4" /></button>
              <span data-testid="product-qty" className="font-mono w-6 text-center">{qty}</span>
              <button data-testid="product-qty-inc" onClick={() => setQty(qty + 1)} className="p-3"><Plus className="w-4 h-4" /></button>
            </div>
            <button data-testid="add-to-cart-btn" onClick={() => add(p, qty)} className="flex-1 min-w-[160px] rounded-full bg-[#0D9488] hover:bg-[#0F766E] text-white py-3.5 font-semibold transition-colors">Add to cart</button>
            {p.is_device && <button data-testid="gift-device-btn" onClick={() => { setGift({ ...gift, enabled: true }); add(p, qty); }} className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#0D9488] text-[#0F766E] px-5 py-3 font-semibold hover:bg-[#0D9488] hover:text-white transition-colors"><Gift className="w-4 h-4" />Send as a gift</button>}
          </div>
          <div className="mt-8 rounded-2xl bg-white border border-[#E4DFD4] p-6">
            <div className="font-semibold text-sm mb-3">What's included</div>
            <ul className="space-y-2">{p.includes.map((x) => <li key={x} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-[#0D9488] shrink-0 mt-0.5" />{x}</li>)}</ul>
          </div>
          <div className="mt-6 grid grid-cols-3 gap-3 text-xs text-slate-600">
            <div className="flex flex-col items-center text-center gap-1.5"><ShieldCheck className="w-5 h-5 text-[#0D9488]" />Secure checkout</div>
            <div className="flex flex-col items-center text-center gap-1.5"><Truck className="w-5 h-5 text-[#0D9488]" />Ships within the US</div>
            <div className="flex flex-col items-center text-center gap-1.5"><Sparkles className="w-5 h-5 text-[#0D9488]" />Free AI planning</div>
          </div>
          <p className="mt-6 text-xs text-amber-800 bg-[#FFFBEB] border border-amber-200 rounded-lg p-3">Not intended to diagnose, treat, cure or prevent any disease. Do not use when pregnant or with a pacemaker or implanted electronic device.</p>
        </div>
      </div>
      <ProductReviews productId={p.id} />
    </div>
  );
}
